import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useParams, useSearchParams } from 'react-router-dom';
import { isAuthenticated, signOut, getOrgId, setOrgId, isSuperAdmin } from './auth';
import StripeConnect from './components/StripeConnect';
import OnboardingWizard from './components/OnboardingWizard';
import { getMe, getSettings, saveSettings } from './api';
import { PreferencesProvider } from './context/PreferencesContext';
import Login from './components/Login';
import Signup from './components/Signup';
import OrgSetup from './components/OrgSetup';
import OrgMembers from './components/OrgMembers';
import AcceptInvite from './components/AcceptInvite';
import Sidebar from './components/Sidebar';
import UserMenu from './components/UserMenu';
import ProjectsDashboard from './components/ProjectsDashboard';
import Dashboard from './components/Dashboard';
import PlayerDetail from './components/PlayerDetail';
import ShareView from './components/ShareView';
import SuperAdmin from './components/SuperAdmin';

// ─── Public Pages ─────────────────────────────────────────────────────────────

function SharePage() {
  const { token } = useParams();
  return <ShareView token={token} />;
}

function InvitePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [page, setPage] = useState('accept');
  const handleAccepted = () => { window.location.href = '/'; };
  if (page === 'signup') return <Signup onSignedUp={() => setPage('accept')} onGoToLogin={() => setPage('login')} />;
  if (page === 'login') return <Login onLogin={() => setPage('accept')} />;
  return <AcceptInvite token={token} onAccepted={handleAccepted} onGoToLogin={() => setPage('login')} onGoToSignup={() => setPage('signup')} />;
}

// ─── Settings Modal ───────────────────────────────────────────────────────────

function SettingsModal({ onClose }) {
  const [form, setForm] = useState({ frontendUrl: '', fromEmail: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    getSettings().then(s => {
      setForm({ frontendUrl: s.frontendUrl || '', fromEmail: s.fromEmail || '' });
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handle = async (e) => {
    e.preventDefault(); setSaving(true);
    await saveSettings(form);
    setSaving(false); setSuccess(true);
    setTimeout(() => { setSuccess(false); onClose(); }, 1000);
  };

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h3>⚙️ Platform Settings</h3>
        <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>Platform-level settings. Venmo is configured per project.</p>
        {loading ? <p style={{ color: 'var(--muted)' }}>Loading…</p> : (
          <form onSubmit={handle}>
            <div className="form-group">
              <label>App URL</label>
              <input value={form.frontendUrl} onChange={set('frontendUrl')} placeholder="https://app.duestrack.com" />
              <div className="helper-text">Used in email links sent to parents.</div>
            </div>
            <div className="form-group">
              <label>From Email</label>
              <input type="email" value={form.fromEmail} onChange={set('fromEmail')} placeholder="noreply@duestrack.com" />
              <div className="helper-text">Must be verified in AWS SES.</div>
            </div>
            {success && <div className="alert alert-success">Saved!</div>}
            <div className="modal-footer">
              <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </form>
        )}
        <StripeConnect />
      </div>
    </div>
  );
}

// ─── Admin App ────────────────────────────────────────────────────────────────

function AdminApp() {
  const [authed, setAuthed] = useState(isAuthenticated());
  const [authPage, setAuthPage] = useState('login');
  const [appState, setAppState] = useState('loading');
  const [orgs, setOrgs] = useState([]);
  const [currentOrgId, setCurrentOrgId] = useState(getOrgId());
  const [userEmail, setUserEmail] = useState('');
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [showSuperAdmin, setShowSuperAdmin] = useState(false);
  const [addNewProject, setAddNewProject] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const clearView = () => { setShowSuperAdmin(false); setShowMembers(false); setSelectedProject(null); setSelectedPlayer(null); };
  const isAdmin = isSuperAdmin();

  useEffect(() => {
    if (!authed) return;
    getMe().then(me => {
      setUserEmail(me.email || me.userId || '');
      if (!me.orgs?.length) { setAppState('onboarding'); return; }
      setOrgs(me.orgs);
      const stored = getOrgId();
      const validOrg = me.orgs.find(o => o.orgId === stored) || me.orgs[0];
      setOrgId(validOrg.orgId);
      setCurrentOrgId(validOrg.orgId);
      setAppState('ready');
    }).catch(() => { signOut(); setAuthed(false); });
  }, [authed]);

  if (!authed) {
    if (authPage === 'signup') return <Signup onSignedUp={() => setAuthed(true)} onGoToLogin={() => setAuthPage('login')} />;
    return <Login onLogin={() => setAuthed(true)} onGoToSignup={() => setAuthPage('signup')} />;
  }

  if (appState === 'loading') return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
      <p style={{ color: 'var(--muted)' }}>Loading…</p>
    </div>
  );

  if (appState === 'onboarding') return (
    <OrgSetup onComplete={(org) => {
      setOrgs([org]);
      setCurrentOrgId(org.orgId);
      setOrgId(org.orgId);
      setAppState('ready');
      setShowOnboarding(true); // Show wizard after org creation
    }} />
  );

  if (showOnboarding) return (
    <OnboardingWizard
      onComplete={() => setShowOnboarding(false)}
      onSkip={() => setShowOnboarding(false)}
    />
  );

  const handleSelectProject = (project) => {
    setSelectedProject(project);
    setSelectedPlayer(null);
    setShowSuperAdmin(false);
    setShowMembers(false);
  };

  const handleImpersonate = (org) => {
    setCurrentOrgId(org.orgId);
    setOrgId(org.orgId);
    setSelectedProject(null);
    setSelectedPlayer(null);
    setShowSuperAdmin(false);
    setProjects([]);
  };

  let mainContent;
  if (showSuperAdmin) {
    mainContent = <SuperAdmin onClose={() => setShowSuperAdmin(false)} onImpersonate={handleImpersonate} />;
  } else if (showMembers) {
    mainContent = <OrgMembers />;
  } else if (selectedPlayer) {
    mainContent = <PlayerDetail playerId={selectedPlayer} onBack={() => setSelectedPlayer(null)} />;
  } else if (selectedProject) {
    mainContent = <Dashboard project={selectedProject} onSelectPlayer={setSelectedPlayer} />;
  } else {
    mainContent = (
      <ProjectsDashboard
        onSelectProject={handleSelectProject}
        onProjectsLoaded={setProjects}
        showAddNew={addNewProject}
        onAddNewHandled={() => setAddNewProject(false)}
      />
    );
  }

  return (
    <div className={`app-layout${sidebarCollapsed ? '' : ''}`}>
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(c => !c)}
        projects={projects}
        selectedProject={selectedProject}
        onSelectProject={handleSelectProject}
        onNewProject={() => { setSelectedProject(null); setSelectedPlayer(null); setAddNewProject(true); }}
        onShowProjects={() => clearView()}
        showingProjects={!showSuperAdmin && !showMembers && !selectedProject && !selectedPlayer}
        onShowMembers={() => { setShowSuperAdmin(false); setSelectedProject(null); setSelectedPlayer(null); setShowMembers(true); }}
        onShowAdmin={() => { setShowSuperAdmin(s => !s); setSelectedProject(null); setSelectedPlayer(null); }}
        showingAdmin={showSuperAdmin}
        showingMembers={showMembers}
      />

      <div className={`app-main${sidebarCollapsed ? ' sidebar-collapsed' : ''}`}>
        <div className="app-topbar">
          {/* Breadcrumb */}
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--muted)' }}>
            {selectedPlayer && (
              <>
                <span style={{ cursor: 'pointer', color: 'var(--navy)' }} onClick={() => setSelectedPlayer(null)}>← Players</span>
              </>
            )}
          </div>

          {/* Org switcher if multiple orgs */}
          {orgs.length > 1 && (
            <select value={currentOrgId || ''} onChange={e => { setOrgId(e.target.value); setCurrentOrgId(e.target.value); setSelectedProject(null); setSelectedPlayer(null); }}
              style={{ background: 'var(--surface-2)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 6, padding: '5px 10px', fontSize: 13, cursor: 'pointer', marginRight: 8 }}>
              {orgs.map(o => <option key={o.orgId} value={o.orgId}>{o.name}</option>)}
            </select>
          )}

          <UserMenu
            email={userEmail}
            onShowSettings={() => setShowSettings(true)}
            onSignOut={() => setAuthed(false)}
          />
        </div>

        <div className="app-content">
          {mainContent}
        </div>
      </div>

      {showSettings && <SettingsModal onClose={() => setShowSettings(false)} />}
    </div>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────

export default function App() {
  return (
    <PreferencesProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/share/:token" element={<SharePage />} />
          <Route path="/accept-invite" element={<InvitePage />} />
          <Route path="*" element={<AdminApp />} />
        </Routes>
      </BrowserRouter>
    </PreferencesProvider>
  );
}
